// Payment Link Service
// Staff-side API for Razorpay payment links: create a link for a booking and
// send it to the patient over SMS / WhatsApp / email, then track / resend / cancel it.
// Backend: smartambulancemvpbackend/src/modules/paymentLink (mounted at /api/payment-links).
import { api, handleApiError, handleApiSuccess } from './api.js';

const BASE = '/payment-links';

export const PAYMENT_LINK_CHANNELS = ['SMS', 'WHATSAPP', 'EMAIL'];

// Links that can still be paid
export const ACTIVE_LINK_STATUSES = ['CREATED', 'PARTIALLY_PAID'];

export const isLinkActive = (link) =>
  !!link && ACTIVE_LINK_STATUSES.includes(link.status) && new Date(link.expiresAt) > new Date();

const call = async (request) => {
  try {
    return handleApiSuccess(await request()).data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Create a payment link for a booking and send it.
 * @param {Object} payload
 * @param {string} payload.bookingId
 * @param {string[]} [payload.channels] - subset of SMS / WHATSAPP / EMAIL (default: every channel with contact details)
 * @param {number} [payload.amount] - rupees; defaults to the booking's fare
 * @param {number} [payload.expiryMinutes] - 16..10080 (default 24h)
 * @param {string} [payload.phone] - override the patient's phone
 * @param {string} [payload.email] - override the patient's email
 * @returns {Promise<{paymentLink: Object, delivery: Object, reused: boolean}>}
 */
export const createPaymentLink = (payload) => call(() => api.post(BASE, payload));

/** @returns {Promise<{paymentLinks: Object[]}>} newest first */
export const listBookingPaymentLinks = (bookingId) =>
  call(() => api.get(`${BASE}/booking/${encodeURIComponent(bookingId)}`));

/** @param {boolean} [refresh] - also re-check the status with Razorpay */
export const getPaymentLink = (linkId, refresh = false) =>
  call(() => api.get(`${BASE}/${linkId}`, { params: refresh ? { refresh: true } : undefined }));

export const resendPaymentLink = (linkId, payload = {}) =>
  call(() => api.post(`${BASE}/${linkId}/resend`, payload));

export const cancelPaymentLink = (linkId) => call(() => api.post(`${BASE}/${linkId}/cancel`));

export default {
  createPaymentLink,
  listBookingPaymentLinks,
  getPaymentLink,
  resendPaymentLink,
  cancelPaymentLink,
  isLinkActive
};
